import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../../shared/ui/FormField';
import { DollarSignIcon, InfoIcon, LinkIcon, FormInputIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, CalendarIcon, ExternalLinkIcon, PlusIcon, ShareIcon, SettingsIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
import { CaptureField, CaptureFieldsEditor } from '../../../shared/ui/CaptureFieldsEditor';
import { ShareLinkModal } from './ShareLinkModal';
interface StaticLink {
  id: string;
  name: string;
  reference: string;
  amount: string;
  currency: string;
  url: string;
  active: boolean;
  createdAt: string;
  usageCount: number;
  totalCollected: number;
  expiryDate?: string;
}
interface FixedLinksPaymentProps {
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function FixedLinksPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: FixedLinksPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [captureFields, setCaptureFields] = useState<CaptureField[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentLinkToShare, setCurrentLinkToShare] = useState<StaticLink | null>(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [existingLinks, setExistingLinks] = useState<StaticLink[]>([{
    id: '1',
    name: 'Monthly Subscription',
    reference: 'monthly-sub',
    amount: '19.99',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/monthly-sub',
    active: true,
    createdAt: '2023-05-15',
    usageCount: 47,
    totalCollected: 939.53,
    expiryDate: '2023-12-31 23:59'
  }, {
    id: '2',
    name: 'Premium Plan',
    reference: 'premium-plan',
    amount: '49.99',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/premium-plan',
    active: true,
    createdAt: '2023-06-22',
    usageCount: 23,
    totalCollected: 1149.77
  }, {
    id: '3',
    name: 'One-time Donation',
    reference: 'donation-form',
    amount: '10.00',
    currency: getTenant().currency.code,
    url: 'https://payment.example.com/pay/donation-form',
    active: false,
    createdAt: '2023-08-10',
    usageCount: 156,
    totalCollected: 1560.0,
    expiryDate: '2023-10-15 18:00'
  }]);
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const toggleLinkStatus = (id: string) => {
    setExistingLinks(existingLinks.map(link => link.id === id ? {
      ...link,
      active: !link.active
    } : link));
  };
  const deleteLink = (id: string) => {
    if (confirm('Are you sure you want to permanently delete this payment link?')) {
      setExistingLinks(existingLinks.filter(link => link.id !== id));
    }
  };
  const copyLinkUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied to clipboard');
    });
  };
  const handleShareLink = (link: StaticLink) => {
    setCurrentLinkToShare(link);
    setShowShareModal(true);
  };
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(part => parseInt(part, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = expiryDate ? parseDate(expiryDate) : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks = [];
    let days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      days.push(date);
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) {
          days.push(null);
        }
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    const date = calendar.month;
    date.setMonth(date.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    if (date) {
      setExpiryDate(formatDate(date));
      setShowDatePicker(false);
    }
  };
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handleCreateLink = () => {
    let expiryDateTimeString = undefined;
    if (hasExpiry && expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(part => part.trim());
      expiryDateTimeString = `${year}-${month}-${day} ${expiryTime}`;
    }
    const newLink: StaticLink = {
      id: `link-${Date.now()}`,
      name: description || 'New Payment Link',
      reference: reference || `link-${Date.now()}`,
      amount: amount || '0.00',
      currency: currency,
      url: `https://payment.example.com/pay/${reference || Date.now()}`,
      active: true,
      createdAt: new Date().toISOString().split('T')[0],
      usageCount: 0,
      totalCollected: 0,
      expiryDate: expiryDateTimeString
    };
    setExistingLinks([newLink, ...existingLinks]);
    setAmount('');
    setReference('');
    setDescription('');
    setCaptureFields([]);
    setHasExpiry(false);
    setExpiryDate('');
    setExpiryTime('23:59');
  };
  const formatExpiryForDisplay = (expiryString?: string) => {
    if (!expiryString) return 'No expiry';
    const [datePart, timePart] = expiryString.split(' ');
    const date = new Date(datePart + 'T' + timePart);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  const isLinkExpired = (expiryString?: string) => {
    if (!expiryString) return false;
    const now = new Date();
    const [datePart, timePart] = expiryString.split(' ');
    const expiryDate = new Date(datePart + 'T' + timePart);
    return now > expiryDate;
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-8091" data-mp="3g5wkrw8ywg2e">
      {/* ─── EXISTING LINKS ─── */}
      {existingLinks.length > 0 && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8092" data-mp="33pnh0pcz5322">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-8093" data-mp="33rrlb8esc42v">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8094" data-mp="3ho518d3o7xf8">
              <LinkIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8095" data-mp="3cq3dvfz7pbxy" />
              {t('components.FixedLinksPayment.your_fixed_payment_links')}
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-8096" data-mp="2kzfjcms1n4t9">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-8097" data-mp="2l0ql6h1xi2hq">
              <thead className="bg-gray-50" data-id="element-8098" data-mp="2yqu16qa7szyq">
                <tr data-id="element-8099" data-mp="2yrve70asjwj3">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8100" data-mp="2tzu51n8x6lxa">
                    {t('components.FixedLinksPayment.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8101" data-mp="267wwq64oic0x">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8102" data-mp="udd7sojeebl9">
                    {t('components.BulkPaymentList.created')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8103" data-mp="lcoszrm6k77b">
                    {t('components.CardOnFilePayment.expires')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8104" data-mp="bmvh0pi4lq2j">
                    {t('components.FixedLinksPayment.usage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8105" data-mp="1ophufnpey1d">
                    {t('components.FixedLinksPayment.total_collected')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8106" data-mp="3otngycsqsfr2">
                    {t('components.BulkPaymentList.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8107" data-mp="29a7dvedty05s">
                    {t('components.BulkPaymentList.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-8108" data-mp="2i9uj6b9ojq44">
                {existingLinks.map(link => <tr key={link.id} className={!link.active || isLinkExpired(link.expiryDate) ? 'bg-gray-50' : ''} data-id="element-8109" data-mp="1lmpv28cjmcey">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8110" data-mp="1zdbf40dqgsef">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8111" data-mp="11wvzrkamdf5z">
                        {link.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-8112" data-mp="16uxho1wb78fv">
                        {link.reference}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8113" data-mp="257t758dbawwg">
                      {t('components.BulkPaymentListSimplified.usd')}{' '}
                      {link.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8114" data-mp="2ex33x1j3p3qw">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-8115" data-mp="2ezz0alfavkd8">
                        <CalendarIcon size={14} className="mr-1.5 text-gray-400" data-id="element-8116" data-mp="1r8ad1jhc5m35" />

                        {link.createdAt}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8117" data-mp="1v9cf082n0c8t">
                      <div className="text-sm text-gray-900 flex items-center" data-id="element-8118" data-mp="1vbykm25ywhoe">
                        <ClockIcon size={14} className="mr-1.5 text-gray-400" data-id="element-8119" data-mp="3bm0zun2l77pn" />
                        {formatExpiryForDisplay(link.expiryDate)}
                        {isLinkExpired(link.expiryDate) && <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded font-medium" data-id="element-8120" data-mp="3f7vwwfs48iih">
                            {t('components.CardOnFilePayment.expired')}
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8121" data-mp="3a8svr6f9awfs">
                      {link.usageCount}{' '}
                      {t('components.FixedLinksPayment.payments')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-8122" data-mp="3o6r49v6wbynd">
                      {formatCurrencyAmount(link.totalCollected, {
                  fractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8123" data-mp="ko6a7t4qxddd">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${link.active && !isLinkExpired(link.expiryDate) ? 'bg-[#E2231A]/10 text-[#E2231A]' : 'bg-gray-100 text-gray-800'}`} data-id="element-8124" data-mp="pgr04jkj76bm">
                        {link.active && !isLinkExpired(link.expiryDate) ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8125" data-mp="3gnrx9wxc5fr8">
                      <div className="flex items-center justify-end space-x-2" data-id="element-8126" data-mp="3boylc8xp5wt6">
                        <button onClick={() => copyLinkUrl(link.url)} className="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" title={t('components.FixedLinksPayment.attrs.copy_link')} data-id="element-8127" data-mp="3braxg5grxcyb">
                          <ExternalLinkIcon size={15} data-id="element-8128" data-mp="3umhtlacbzbej" />
                        </button>
                        <button onClick={() => handleShareLink(link)} className="p-1.5 rounded-md bg-[#E2231A]/10 text-[#E2231A] hover:bg-[#E2231A]/20 transition-colors" title={t('components.FixedLinksPayment.attrs.share_link')} data-id="element-8129" data-mp="5v4dhbj18ly1">
                          <ShareIcon size={15} data-id="element-8130" data-mp="ornq4at1orzc" />
                        </button>
                        <button onClick={() => toggleLinkStatus(link.id)} className={`p-1.5 rounded-md transition-colors ${link.active ? 'bg-[#E2231A]/10 text-[#E2231A] hover:bg-[#E2231A]/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={link.active ? 'Disable link' : 'Enable link'} data-id="element-8131" data-mp="tqqk907hldsj">
                          {link.active ? <ToggleRightIcon size={15} data-id="element-8132" data-mp="1cfx8t3uyozm9" /> : <ToggleLeftIcon size={15} data-id="element-8133" data-mp="39iw082xka1te" />}
                        </button>
                        <button onClick={() => deleteLink(link.id)} className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title={t('components.FixedLinksPayment.attrs.delete_link_permanently')} data-id="element-8134" data-mp="34hggqfb183d4">
                          <TrashIcon size={15} data-id="element-8135" data-mp="3n9smzd4t3osj" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {existingLinks.length === 0 && <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-8136" data-mp="26wauzw1zk1t1">
          <p className="text-gray-500 font-medium" data-id="element-8137" data-mp="22uz7bw2o9qnh">
            {t('components.FixedLinksPayment.you_havent_created_any_fixed_payment')}
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-8138" data-mp="1wrm5djnojvi9">
            {t('components.FixedLinksPayment.fill_out_the_form_below_to')}
          </p>
        </div>}

      {/* Card 1: Payment details */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8139" data-mp="1nzadrugty20p">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8140" data-mp="1929odxm42j5e">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8141" data-mp="190q8i84daa45">
            <LinkIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8142" data-mp="1e25rlumzi6jw" />
            {t('components.FixedLinksPayment.payment_details')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-8143" data-mp="150obic9ku4v7">
          <div data-id="element-8144" data-mp="151hcsxpro25y">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8145" data-mp="1517ulwayqzat">
              {t('components.CardOnFilePayment.payment_amount')}{' '}
              <span className="text-red-400" data-id="element-8146" data-mp="2zdnzots0w7e3">*</span>
            </label>
            <div className="relative" data-id="element-8147" data-mp="bth4eb8ymkp7">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8148" data-mp="bsfjlf256jua">
                <span className="text-sm font-medium text-gray-500" data-id="element-8149" data-mp="6vywmo6oibis">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12 pr-20`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-8150" data-mp="3u8jbra2v0uoe" />

              <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-8151" data-mp="3kbwrameg45mw">
                <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-lg focus:ring-[#E2231A]/20 focus:border-[#E2231A]" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-8152" data-mp="3faqxel91ucbs">
                  <option data-id="element-8153" data-mp="19foajpkwwl3o">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </option>
                  <option data-id="element-8154" data-mp="2wdyrw88a5bt2">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </option>
                  <option data-id="element-8155" data-mp="31603drxmiyi1">{t('components.FixedLinksPayment.eur')}</option>
                  <option data-id="element-8156" data-mp="356ajhxf4r5am">{t('components.FixedLinksPayment.gbp')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8157" data-mp="28huin56kl9i7">
            <div data-id="element-8158" data-mp="2dekybregq333">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8159" data-mp="2dg5t9i4ovo1h">
                {t('components.FixedLinksPayment.link_reference')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.eg_monthlysubscription_or_donationform')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-8160" data-mp="2mh40e9afcboa" />
            </div>
            <div data-id="element-8161" data-mp="854akhtozkv0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8162" data-mp="84kt127yeyhi">
                {t('components.FixedLinksPayment.payment_description')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.eg_monthly_subscription_or_donation')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-8163" data-mp="hue31ysg75jn" />
            </div>
          </div>

          {/* Expiry toggle */}
          <div data-id="element-8164" data-mp="3vb3moozb3z52">
            <div className="flex items-center" data-id="element-8165" data-mp="3vbn84fo615nu">
              <label className="inline-flex items-center cursor-pointer" data-id="element-8166" data-mp="15ljkgie1e5w2">
                <input type="checkbox" className="sr-only" checked={hasExpiry} onChange={() => setHasExpiry(!hasExpiry)} data-id="element-8167" data-mp="1ak36eismrhof" />

                <div className={`w-10 h-5 rounded-full transition-colors ${hasExpiry ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-8168" data-mp="2q3av1kvrujwe">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${hasExpiry ? 'translate-x-5' : ''}`} data-id="element-8169" data-mp="2l9zlhbzkrp0s" />
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-8170" data-mp="2bbtkqxpi8oxi">
                  {t('components.FixedLinksPayment.set_expiration_date_and_time')}
                </span>
              </label>
              <InfoTooltip content="Fixed links don't expire by default. Enable this to set an optional expiration date and time." data-id="element-8171" data-mp="1xf6o6w1gs0s4" />
            </div>

            {hasExpiry && <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8172" data-mp="3rso3jvrptrc2">
                <div className="relative" data-id="element-8173" data-mp="3rqlfhha4i89z">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8174" data-mp="1q2z24lt03pdm">
                    {t('components.FixedLinksPayment.expiry_date')}
                  </label>
                  <div className="relative" data-id="element-8175" data-mp="1g642u0owlkya">
                    <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-8176" data-mp="1g2yfoqzu4t57" />

                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8177" data-mp="23udi0jrm3agg">
                      <CalendarIcon size={16} className="text-gray-400" data-id="element-8178" data-mp="nfvt8x8fo8vb" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-8179" data-mp="sfzyfr3w5h68">
                      {showDatePicker ? <ChevronUpIcon size={16} className="text-gray-400" data-id="element-8180" data-mp="31q93bes5zozq" /> : <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8181" data-mp="3fllawmrf59y2" />}
                    </div>
                  </div>

                  {showDatePicker && <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-full" data-id="element-8182" data-mp="2nunk5o8m3zf8">
                      <div className="flex justify-between items-center mb-2" data-id="element-8183" data-mp="2wp1z1pp8d7xt">
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-8184" data-mp="2wr66ghmc8p2u">
                          <ChevronDownIcon size={16} className="transform rotate-90" data-id="element-8185" data-mp="207fdb1vjcib3" />
                        </button>
                        <div className="font-medium text-sm" data-id="element-8186" data-mp="2hv8217kqkg1a">
                          {calendar.month.toLocaleString('default', {
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                        <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-8187" data-mp="2rn44eutuusbb">
                          <ChevronDownIcon size={16} className="transform -rotate-90" data-id="element-8188" data-mp="35lbxgrnrt9h7" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs mb-1" data-id="element-8189" data-mp="1b82qb6ip6024">
                        <div className="text-gray-500" data-id="element-8190" data-mp="1g5ci2stk6nt4">
                          {t('components.FixedLinksPayment.su')}
                        </div>
                        <div className="text-gray-500" data-id="element-8191" data-mp="18ofwvuhelvpk">
                          {t('components.FixedLinksPayment.mo')}
                        </div>
                        <div className="text-gray-500" data-id="element-8192" data-mp="yxv26i4upbbx">
                          {t('components.FixedLinksPayment.tu')}
                        </div>
                        <div className="text-gray-500" data-id="element-8193" data-mp="tvvy161aeadx">
                          {t('components.FixedLinksPayment.we')}
                        </div>
                        <div className="text-gray-500" data-id="element-8194" data-mp="1mnfmb4cqqems">
                          {t('components.FixedLinksPayment.th')}
                        </div>
                        <div className="text-gray-500" data-id="element-8195" data-mp="1hp48kijbrjza">
                          {t('components.FixedLinksPayment.fr')}
                        </div>
                        <div className="text-gray-500" data-id="element-8196" data-mp="68xj4cstfai4">
                          {t('components.FixedLinksPayment.sa')}
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center" data-id="element-8197" data-mp="2pego2inxffmr">
                        {calendar.weeks.flatMap(week => week.map((date, index) => date ? <button key={index} type="button" disabled={isPast(date)} className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-sm
                                  ${isSelected(date) ? 'bg-[#E2231A] text-white' : ''}
                                  ${isToday(date) && !isSelected(date) ? 'border border-[#E2231A] text-[#E2231A]' : ''}
                                  ${isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                                `} onClick={() => handleDateSelect(date)} data-id="element-8198" data-mp="2fg118rvxmsj2">
                                {date.getDate()}
                              </button> : <div key={index} className="w-8 h-8" data-id="element-8199" data-mp="145fhnb71b1wp"></div>))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between" data-id="element-8200" data-mp="1d3how8hd2qsf">
                        <button type="button" className="text-xs font-medium text-[#E2231A] hover:text-[#C41B14]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 7);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-8201" data-mp="1r38xtut0tgzk">
                          {t('components.FixedLinksPayment.7_days')}
                        </button>
                        <button type="button" className="text-xs font-medium text-[#E2231A] hover:text-[#C41B14]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 30);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-8202" data-mp="qhnkfjytpzc3">
                          {t('components.BulkPaymentPreview.30_days')}
                        </button>
                        <button type="button" className="text-xs font-medium text-[#E2231A] hover:text-[#C41B14]" onClick={() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 90);
                    setExpiryDate(formatDate(date));
                    setShowDatePicker(false);
                  }} data-id="element-8203" data-mp="hoakvz697zh1">
                          {t('components.FixedLinksPayment.90_days')}
                        </button>
                      </div>
                    </div>}
                </div>

                <div data-id="element-8204" data-mp="1t9qcg9zljted">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8205" data-mp="1t8f0hmvzzfc6">
                    {t('components.FixedLinksPayment.expiry_time')}
                  </label>
                  <div className="relative" data-id="element-8206" data-mp="1jilztgxe8kmp">
                    <input type="time" className={`${inputClass} pl-10`} value={expiryTime} onChange={e => setExpiryTime(e.target.value)} data-id="element-8207" data-mp="1jhu508bmeqxh" />

                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8208" data-mp="15iuh52vxmg2d">
                      <ClockIcon size={16} className="text-gray-400" data-id="element-8209" data-mp="10m412fk4nkgy" />
                    </div>
                  </div>
                  <div className="mt-1.5 text-xs text-gray-500" data-id="element-8210" data-mp="aeo7gx7jxchl">
                    {t('components.FixedLinksPayment.time_is_in_your_local_timezone')}
                    {Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Card 2: Customer information */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8211" data-mp="3iwawyxzyxk9t">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8212" data-mp="3iu7yrqonwku0">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8213" data-mp="j3bbfp5z6fr">
            <FormInputIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8214" data-mp="3rwqvdt1uu17t" />
            {t('components.FixedLinksPayment.customer_information')}
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8215" data-mp="345bt20a2vjwk">
            {t('components.FixedLinksPayment.specify_what_information_to_collect_from')}
          </p>
        </div>
        <div className="p-6" data-id="element-8216" data-mp="1eo6i4g8n0uym">
          <CaptureFieldsEditor fields={captureFields} onChange={setCaptureFields} data-id="element-8217" data-mp="1jhj2ufa74hm3" />
        </div>
      </div>

      {/* Card 3: Additional options */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8218" data-mp="1aop9jdxwzhp8">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8219" data-mp="1ambf97b269iq">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8220" data-mp="1yfl8qw4p9f6i">
            <SettingsIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8221" data-mp="22g55hyld2gs4" />
            {t('components.CommonPaymentOptions.additional_options')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-8222" data-mp="1tf6wt2zz9l37">
          <div data-id="element-8223" data-mp="1te4az9hii8u5">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8224" data-mp="1tdm4zl51sg91">
              {t('components.CommonPaymentOptions.success_redirect_url')}
            </label>
            <input type="text" className={inputClass} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} data-id="element-8225" data-mp="i1larqpezzlw" />
          </div>

          <div data-id="element-8226" data-mp="3lagfao5ffj0a">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8227" data-mp="3l9y9azsypqf6">
              {t('components.FixedLinksPayment.custom_metadata')}
            </label>
            <button type="button" onClick={() => setMetadata([...metadata, {
            key: '',
            value: ''
          }])} className="inline-flex items-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E2231A]/20 transition-colors" data-id="element-8228" data-mp="3bd2699lu1wi0">
              <PlusIcon size={16} className="mr-1.5 text-gray-400" data-id="element-8229" data-mp="vmfn3qf7z4km" />
              {t('components.FixedLinksPayment.add_metadata_field')}
            </button>

            {metadata.length > 0 && <div className="mt-3 space-y-3" data-id="element-8230" data-mp="182ffofutuezb">
                {metadata.map((item, index) => <div key={index} className="flex space-x-2" data-id="element-8231" data-mp="1cvrzmcok9xlq" data-mpi={index}>
                    <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.key')} value={item.key} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].key = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-8232" data-mp="1ht1n3mse3z6x" />

                    <input type="text" className={inputClass} placeholder={t('components.FixedLinksPayment.attrs.value')} value={item.value} onChange={e => {
                const updatedMetadata = [...metadata];
                updatedMetadata[index].value = e.target.value;
                setMetadata(updatedMetadata);
              }} data-id="element-8233" data-mp="25kfiy2pr2cwn" />

                    <button type="button" onClick={() => {
                const updatedMetadata = [...metadata];
                updatedMetadata.splice(index, 1);
                setMetadata(updatedMetadata);
              }} className="p-2.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50" data-id="element-8234" data-mp="2d5jbp5tvpvno">
                      <TrashIcon size={16} data-id="element-8235" data-mp="39so079uxhbdd" />
                    </button>
                  </div>)}
              </div>}
          </div>
        </div>
      </div>

      {/* Share Link Modal */}
      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} recipients={[]} data-id="element-8236" data-mp="3oe1t744co2oy" />}
    </div>;
}
// Helper component for the info tooltip
function InfoTooltip({
  content
}: {
  content: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative ml-1.5" data-id="element-8237" data-mp="3kcq3kylh7hgr">
      <div className="text-gray-400 hover:text-gray-500 cursor-help" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} data-id="element-8238" data-mp="35pwre2796rss">
        <InfoIcon size={14} data-id="element-8239" data-mp="2woyr9uzxr4fh" />
      </div>
      {showTooltip && <div className="absolute z-10 w-64 px-3 py-2 text-sm text-left font-normal text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -top-2 left-6" data-id="element-8240" data-mp="2wqt930fwugfc">
          {content}
        </div>}
    </div>;
}